import { convexQuery } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  storefrontAdminGetDeliveryZones,
  storefrontAdminUpdateDeliveryZones,
  storefrontAdminUpdateLocationCoordinates,
} from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";

import { Button } from "../ui/button";
import { Input } from "../ui/input";

// ─── Types ──────────────────────────────────────────

interface ZoneRow {
  name: string;
  minRadiusMiles: number;
  maxRadiusMiles: number;
  fee: number; // cents
}

// ─── Component ──────────────────────────────────────

export function DeliveryZonesEditor({ locationId }: { locationId: string }) {
  const typedId = toConvexTableId("locations", locationId);

  const { data, isLoading } = useQuery(
    convexQuery(storefrontAdminGetDeliveryZones, {
      locationId: typedId,
    })
  );

  const updateZonesMutation = useMutation({
    mutationFn: (zones: ZoneRow[]) =>
      convexClient.mutation(storefrontAdminUpdateDeliveryZones, {
        locationId: typedId,
        zones,
      }),
    onSuccess: () => {
      toast.success("Delivery zones saved");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to save zones"
      );
    },
  });

  const updateCoordsMutation = useMutation({
    mutationFn: (coords: { latitude: number; longitude: number }) =>
      convexClient.mutation(storefrontAdminUpdateLocationCoordinates, {
        locationId: typedId,
        ...coords,
      }),
    onSuccess: () => {
      toast.success("Coordinates saved");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to save coordinates"
      );
    },
  });

  // Local editing state — initialized from server data
  const [zones, setZones] = useState<ZoneRow[] | null>(null);
  const [latInput, setLatInput] = useState("");
  const [lngInput, setLngInput] = useState("");

  // Sync server data into local state on first load
  const serverZones = data?.zones ?? [];
  const editingZones = zones ?? serverZones;

  const coords = data?.coordinates ?? null;

  if (isLoading) {
    return <div className="bg-muted h-32 animate-pulse rounded-lg" />;
  }

  const handleAddZone = () => {
    const last = editingZones.at(-1);
    const nextMin = last?.maxRadiusMiles ?? 0;
    setZones([
      ...editingZones,
      {
        name: `Zone ${editingZones.length + 1}`,
        minRadiusMiles: nextMin,
        maxRadiusMiles: nextMin + 3,
        fee: 0,
      },
    ]);
  };

  const handleRemoveZone = (index: number) => {
    const updated = editingZones.filter((_, i) => i !== index);
    // Re-link contiguous minimums
    for (const [i, zone] of updated.entries()) {
      const prev = updated[i - 1];
      if (i > 0 && prev) {
        updated[i] = { ...zone, minRadiusMiles: prev.maxRadiusMiles };
      }
    }
    setZones(updated);
  };

  const handleZoneChange = (
    index: number,
    field: keyof ZoneRow,
    value: string
  ) => {
    const updated = [...editingZones];
    const current = updated[index];
    if (!current) {
      return;
    }

    if (field === "name") {
      updated[index] = { ...current, name: value };
    } else if (field === "fee") {
      updated[index] = {
        ...current,
        fee: Math.round(Number.parseFloat(value || "0") * 100),
      };
    } else if (field === "maxRadiusMiles") {
      const max = Number.parseFloat(value || "0");
      updated[index] = { ...current, maxRadiusMiles: max };
      // Auto-link next zone's min
      const next = updated[index + 1];
      if (next) {
        updated[index + 1] = { ...next, minRadiusMiles: max };
      }
    }
    setZones(updated);
  };

  const handleSaveZones = () => {
    updateZonesMutation.mutate(editingZones);
    setZones(null); // Reset to track server state
  };

  const handleSaveCoords = () => {
    const lat = Number.parseFloat(latInput);
    const lng = Number.parseFloat(lngInput);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error("Please enter valid latitude and longitude values");
      return;
    }
    updateCoordsMutation.mutate({ latitude: lat, longitude: lng });
    setLatInput("");
    setLngInput("");
  };

  const isDirty = zones !== null;

  return (
    <section>
      <div className="space-y-1 py-4">
        <h2 className="subtitle-sm text-foreground">Delivery Zones</h2>
        <p className="body-sm text-muted-foreground">
          Set distance-based delivery fees. Zones are measured from your
          location.
        </p>
      </div>

      {/* Location coordinates */}
      <div className="mb-4 rounded-lg border p-4">
        <h3 className="mb-2 text-sm font-medium">Location</h3>
        {coords ? (
          <p className="text-muted-foreground mb-2 text-sm">
            Current: {coords.latitude.toFixed(4)}, {coords.longitude.toFixed(4)}
          </p>
        ) : (
          <p className="text-warning mb-2 text-sm">
            No coordinates set. Delivery zones require a location.
          </p>
        )}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label
              className="text-muted-foreground mb-1 block text-xs"
              htmlFor="lat-input"
            >
              Latitude
            </label>
            <Input
              id="lat-input"
              onChange={(e) => setLatInput(e.target.value)}
              placeholder={coords?.latitude.toString() ?? "37.7749"}
              step="0.0001"
              type="number"
              value={latInput}
            />
          </div>
          <div className="flex-1">
            <label
              className="text-muted-foreground mb-1 block text-xs"
              htmlFor="lng-input"
            >
              Longitude
            </label>
            <Input
              id="lng-input"
              onChange={(e) => setLngInput(e.target.value)}
              placeholder={coords?.longitude.toString() ?? "-122.4194"}
              step="0.0001"
              type="number"
              value={lngInput}
            />
          </div>
          <Button
            disabled={updateCoordsMutation.isPending || !(latInput || lngInput)}
            onClick={handleSaveCoords}
            size="sm"
            type="button"
            variant="outline"
          >
            {updateCoordsMutation.isPending ? "Saving..." : "Update"}
          </Button>
        </div>
      </div>

      {/* Zone list */}
      <div className="space-y-2">
        {editingZones.map((zone, index) => {
          const zoneKey = `zone-${zone.minRadiusMiles}-${zone.maxRadiusMiles}`;
          return (
            <div
              className="flex items-end gap-2 rounded-lg border p-3"
              key={zoneKey}
            >
              <div className="flex-1">
                {index === 0 && (
                  <label
                    className="text-muted-foreground mb-1 block text-xs"
                    htmlFor={`${zoneKey}-name`}
                  >
                    Name
                  </label>
                )}
                <Input
                  id={`${zoneKey}-name`}
                  onChange={(e) =>
                    handleZoneChange(index, "name", e.target.value)
                  }
                  placeholder="Zone name"
                  value={zone.name}
                />
              </div>
              <div className="w-20">
                {index === 0 && (
                  <label
                    className="text-muted-foreground mb-1 block text-xs"
                    htmlFor={`${zoneKey}-min`}
                  >
                    From (mi)
                  </label>
                )}
                <Input
                  className="bg-muted text-muted-foreground"
                  disabled
                  id={`${zoneKey}-min`}
                  type="number"
                  value={zone.minRadiusMiles}
                />
              </div>
              <div className="w-20">
                {index === 0 && (
                  <label
                    className="text-muted-foreground mb-1 block text-xs"
                    htmlFor={`${zoneKey}-max`}
                  >
                    To (mi)
                  </label>
                )}
                <Input
                  id={`${zoneKey}-max`}
                  onChange={(e) =>
                    handleZoneChange(index, "maxRadiusMiles", e.target.value)
                  }
                  step="0.5"
                  type="number"
                  value={zone.maxRadiusMiles}
                />
              </div>
              <div className="w-24">
                {index === 0 && (
                  <label
                    className="text-muted-foreground mb-1 block text-xs"
                    htmlFor={`${zoneKey}-fee`}
                  >
                    Fee ($)
                  </label>
                )}
                <Input
                  id={`${zoneKey}-fee`}
                  onChange={(e) =>
                    handleZoneChange(index, "fee", e.target.value)
                  }
                  step="0.01"
                  type="number"
                  value={(zone.fee / 100).toFixed(2)}
                />
              </div>
              <Button
                aria-label={`Remove ${zone.name}`}
                onClick={() => handleRemoveZone(index)}
                size="icon"
                type="button"
                variant="ghost"
              >
                <Trash2 className="text-muted-foreground size-4" />
              </Button>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="mt-3 flex gap-2">
        <Button
          onClick={handleAddZone}
          size="sm"
          type="button"
          variant="outline"
        >
          <Plus className="mr-1 size-4" />
          Add Zone
        </Button>
        {isDirty && (
          <Button
            disabled={updateZonesMutation.isPending}
            onClick={handleSaveZones}
            size="sm"
            type="button"
          >
            {updateZonesMutation.isPending ? "Saving..." : "Save Zones"}
          </Button>
        )}
      </div>

      {editingZones.length === 0 && (
        <p className="text-muted-foreground mt-3 text-sm">
          No zones configured. The flat delivery fee from settings above will be
          used.
        </p>
      )}
    </section>
  );
}
